import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";

const SupplierProfileAdd = () => {
  const Navigate = useNavigate();
  const token = localStorage.getItem("Token");

  const [user, setUser] = useState(null);
  const [updatedFields, setUpdatedFields] = useState({});
  const [isModified, setIsModified] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [profilePreview, setProfilePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // ✅ Fetch profile
  const getProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.data) {
        const profile = res.data.data;
        const addressData = profile.address || {};
        const bankDetails = profile.bankDetails || {};

        const merged = {
          ...profile,
          address: addressData.address || "",
          city: addressData.city || "",
          state: addressData.state || "",
          country: addressData.country || "",
          pincode: addressData.pincode || "",
          account_holder_name: bankDetails.account_holder_name || "",
          bank_name: bankDetails.bank_name || "",
          account_number: bankDetails.account_number || "",
          ifsc_code: bankDetails.ifsc_code || "",
          branch_name: bankDetails.branch_name || "",
        };

        setUser(merged);
        localStorage.setItem("user", JSON.stringify(merged));
      }
    } catch (err) {
      if (err.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      } else {
        toast.error("Failed to fetch profile");
      }
    }
  }, [token, Navigate]);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
    getProfile();
  }, [getProfile]);

  // ✅ Handle field input
  const handleInputChange = (e, field) => {
    const { value } = e.target;
    setUpdatedFields((prev) => ({ ...prev, [field]: value }));
    setUser((prev) => ({ ...prev, [field]: value }));
    setIsModified(true);
  };

  // ✅ Handle image upload
  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload a valid image file");
        return;
      }
      if (file.size / 1024 / 1024 > 2) {
        toast.error("Image size must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (type === "profile") {
          setProfilePreview(ev.target.result);
          setProfileFile(file);
        } else {
          setLogoPreview(ev.target.result);
          setLogoFile(file);
        }
      };
      reader.readAsDataURL(file);
      setIsModified(true);
    }
  };

  // ✅ Handle save/update
  const handleSave = async () => {
    if (!isModified && !profileFile && !logoFile) {
      toast.info("No changes to save");
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();

      // add updated fields
      Object.keys(updatedFields).forEach((key) => {
        const value = updatedFields[key];
        if (value !== null && value !== undefined) formData.append(key, value);
      });

      // images
      if (profileFile) formData.append("profile", profileFile);
      if (logoFile) formData.append("logo", logoFile);

      const res = await axios.put(
        `${process.env.REACT_APP_URL}/auth/profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 200) {
        toast.success("Profile updated successfully");
        await getProfile();
        setIsModified(false);
        setUpdatedFields({});
        setProfileFile(null);
        setLogoFile(null);
        setProfilePreview(null);
        setLogoPreview(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } finally {
      setUploading(false);
    }
  };

  if (!user)
    return (
      <div className="al">
        <div className="al-card" style={{ textAlign: "center", padding: "48px 20px", color: "var(--color-text-secondary)" }}>Loading...</div>
      </div>
    );

  const isSupplier = user?.user_type === "supplier";

  return (
    <div className="al">
      <Toaster position="top-center" />

      <div className="al-head">
        <div>
          <h1 className="al-title">Update Profile</h1>
          <p className="al-sub">Manage your supplier account, company and bank details.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-idbanner">
        <label htmlFor="profileUpload" className="image-container" style={{ cursor: "pointer", position: "relative", width: 54, height: 54, borderRadius: 14, overflow: "hidden", flex: "0 0 54px" }}>
          <img
            src={profilePreview ? profilePreview : user?.profile_image ? `${process.env.REACT_APP_IMAGE_URL}${user.profile_image}` : "/assets/img/profileImage.jpg"}
            alt="Profile"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div className="overlay"><FaEdit style={{ color: "#fff", fontSize: 16 }} /></div>
        </label>
        <input type="file" id="profileUpload" style={{ display: "none" }} accept="image/*" onChange={(e) => handleImageChange(e, "profile")} />
        <div>
          <div className="al-idbanner__name">{user?.name || "—"}</div>
          <div className="al-idbanner__sub">{user?.email || "—"}</div>
        </div>
        {isSupplier && (
          <label htmlFor="logoUpload" className="image-container" style={{ marginLeft: "auto", cursor: "pointer", position: "relative", width: 54, height: 54, borderRadius: 14, overflow: "hidden", border: "1px solid var(--color-border)" }} title="Company logo">
            <img
              src={logoPreview ? logoPreview : user?.logo_image ? `${process.env.REACT_APP_IMAGE_URL}${user.logo_image}` : "/assets/img/profileImage.jpg"}
              alt="Logo"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div className="overlay"><FaEdit style={{ color: "#fff", fontSize: 14 }} /></div>
          </label>
        )}
        {isSupplier && (
          <input type="file" id="logoUpload" style={{ display: "none" }} accept="image/*" onChange={(e) => handleImageChange(e, "logo")} />
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        {/* Personal Information */}
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></div>
            <h3 className="al-cardhead__title">Personal Information</h3>
          </div>
          <div className="al-form">
            {[
              { label: "Name", field: "name" },
              { label: "Email", field: "email", readOnly: true },
              { label: "Mobile Number", field: "phone" },
            ].map(({ label, field, readOnly }) => (
              <div key={field}>
                <label className="al-label">{label}</label>
                <input
                  type="text"
                  className="al-input"
                  readOnly={readOnly}
                  style={readOnly ? { background: "var(--color-surface-2)", cursor: "not-allowed" } : undefined}
                  value={user[field] || ""}
                  onChange={(e) => handleInputChange(e, field)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Company Details */}
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg></div>
            <h3 className="al-cardhead__title">Company Details</h3>
          </div>
          <div className="al-form">
            {["company_name", "gst_number", "pan_number", "website", "address", "country", "state", "city", "pincode"].map((field) => (
              <div key={field}>
                <label className="al-label">{field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</label>
                <input
                  type="text"
                  className="al-input"
                  style={["gst_number", "pan_number", "pincode"].includes(field) ? { fontFamily: "var(--font-mono)" } : undefined}
                  value={user[field] || ""}
                  onChange={(e) => handleInputChange(e, field)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Bank Details */}
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg></div>
            <h3 className="al-cardhead__title">Bank Details</h3>
          </div>
          <div className="al-form">
            {[
              { label: "Account Holder Name", field: "account_holder_name" },
              { label: "Bank Name", field: "bank_name" },
              { label: "Account Number", field: "account_number" },
              { label: "IFSC Code", field: "ifsc_code" },
              { label: "Branch Name", field: "branch_name" },
            ].map(({ label, field }) => (
              <div key={field}>
                <label className="al-label">{label}</label>
                <input
                  type="text"
                  className="al-input"
                  style={["account_number", "ifsc_code"].includes(field) ? { fontFamily: "var(--font-mono)" } : undefined}
                  value={user[field] || ""}
                  onChange={(e) => handleInputChange(e, field)}
                />
              </div>
            ))}
          </div>
          <div className="al-actionbar">
            <button
              type="button"
              className="al-btn al-btn--primary"
              disabled={!isModified || uploading}
              style={!isModified || uploading ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onClick={handleSave}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {uploading ? "Saving..." : "Update Profile"}
            </button>
          </div>
        </div>
      </form>

      <style>{`
        .image-container:hover .overlay { opacity: 1; }
        .overlay {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.4);
          display: flex; align-items: center; justify-content: center;
          opacity: 0; transition: opacity 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default SupplierProfileAdd;
